import { clsx } from 'clsx';
import {
  getIconByExtension,
  UploadingFileIcon,
} from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/components/CurrentKnowledgeBaseIcons/CurrentKnowledgeBaseIcons';
import { EditFilePopup } from 'src/pages/OnboardingPage/OnboardingSteps/OnboardingStep2/EditFilePopup/EditFilePopup';
import { DeleteKnowledgeBaseItemModal } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/delete/DeleteKnowledgeBaseItemModal/DeleteKnowledgeBaseItemModal';
import { useLoader } from 'src/provider/LoaderProvider';
import { useDeleteKnowledgeBaseFilesMutation } from 'src/services';
import { UploadErrorToolTip } from 'src/pages/OnboardingPage/OnboardingSteps/OnboardingStep2/UploadErrorToolTip/UploadErrorToolTip';
import { FILE_STATE } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/CurrentKnowledgeBaseConstants';
import type { KnowledgeBase } from 'src/services/knowledgeBaseService';
import type { KBFile } from 'src/utils/fileUtils';

export const UploadFileItem = ({
  item,
  knowledgeBase,
}: {
  item: KBFile;
  knowledgeBase: Pick<KnowledgeBase, 'id'>;
}) => {
  const loader = useLoader();
  const is = {
    uploading: item.state === FILE_STATE.UPLOADING,
    deleting: item.state === FILE_STATE.DELETING,
    error: [FILE_STATE.DELETE_ERROR, FILE_STATE.UPLOAD_ERROR].includes(item.state),
  };
  const [deleteKnowledgeBaseFiles] = useDeleteKnowledgeBaseFilesMutation();

  const onDelete = async (item: KBFile) => {
    if (item) {
      loader.show();
      await deleteKnowledgeBaseFiles({
        id: knowledgeBase.id,
        body: [item.id],
      });
      loader.hide();
    }
  };

  return (
    <div
      className={clsx(
        '-mx-5 pr-5 py-4 bg-[#F1F6F6] flex flex-row px-5 items-center justify-between gap-4',
        (is.uploading || is.deleting) && 'opacity-50 pointer-events-none',
        is.error && 'bg-[#FEE]',
      )}
    >
      <div>
        {is.error && (
          <UploadErrorToolTip
            side={'top'}
            trigger={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="15"
                height="16"
                viewBox="0 0 11 12"
                fill="none"
                className={'cursor-pointer'}
              >
                <path
                  d="M5.5 0.5C6.00488 0.5 6.49186 0.564453 6.96094 0.693359C7.43001 0.822266 7.86686 1.00846 8.27148 1.25195C8.67611 1.49544 9.04671 1.7819 9.3833 2.11133C9.71989 2.44076 10.0081 2.81136 10.248 3.22314C10.488 3.63493 10.6724 4.07357 10.8013 4.53906C10.9302 5.00456 10.9964 5.49154 11 6C11 6.50488 10.9355 6.99186 10.8066 7.46094C10.6777 7.93001 10.4915 8.36686 10.248 8.77148C10.0046 9.17611 9.7181 9.54671 9.38867 9.8833C9.05924 10.2199 8.68864 10.5081 8.27686 10.748C7.86507 10.988 7.42643 11.1724 6.96094 11.3013C6.49544 11.4302 6.00846 11.4964 5.5 11.5C4.99512 11.5 4.50814 11.4355 4.03906 11.3066C3.56999 11.1777 3.13314 10.9915 2.72852 10.748C2.32389 10.5046 1.95329 10.2181 1.6167 9.88867C1.28011 9.55924 0.991862 9.18864 0.751953 8.77686C0.512044 8.36507 0.327637 7.92643 0.19873 7.46094C0.0698242 6.99544 0.00358073 6.50846 0 6C0 5.49512 0.0644531 5.00814 0.193359 4.53906C0.322266 4.06999 0.508464 3.63314 0.751953 3.22852C0.995443 2.82389 1.2819 2.45329 1.61133 2.1167C1.94076 1.78011 2.31136 1.49186 2.72314 1.25195C3.13493 1.01204 3.57357 0.827637 4.03906 0.69873C4.50456 0.569824 4.99154 0.503581 5.5 0.5ZM6.1875 8.75V7.375H4.8125V8.75H6.1875ZM6.1875 6.6875V3.25H4.8125V6.6875H6.1875Z"
                  fill="#FF5151"
                />
              </svg>
            }
            text={`Unable to upload this file`}
          />
        )}
        {!is.error &&
          (is.uploading || is.deleting ? <UploadingFileIcon /> : getIconByExtension(item.name))}
      </div>
      <div className={'block w-full min-w-0'}>
        {item?.name && (
          <p
            className={clsx(
              'whitespace-nowrap text-ellipsis overflow-hidden font-medium',
              is.error && 'text-[#FF5151]',
            )}
          >
            {item.name}
          </p>
        )}
      </div>
      <div className={'flex flex-row gap-7'}>
        <div className={'flex items-center opacity-50 hover:opacity-100 cursor-pointer'}>
          {!is.error && <EditFilePopup item={item} knowledgeBase={knowledgeBase} />}
        </div>
        <div className={'flex items-center'}>
          <DeleteKnowledgeBaseItemModal
            handleSingleItemDelete={onDelete}
            item={{ ...item, data: item }}
          />
        </div>
      </div>
    </div>
  );
};
